import { SPOTLIGHT_COPY } from "./spotlight.constants.ts";
import type { SpotlightItem } from "./spotlight.model.ts";
import { useDecodedImage } from "./useDecodedImage.ts";

/**
 * A TIFF or a HEIC, on screen.
 *
 * Its own component because it is the one slide with state: the decode runs
 * while the slide is mounted, and the library mounts only the slides near the
 * one being read. A `render.slide` function cannot hold a hook.
 *
 * The failed branch is the `unavailable` slide's markup on purpose. To a reader
 * "this could not be converted" and "your browser cannot show this" end in the
 * same place — the download — and giving them two different-looking panels
 * would suggest a difference they cannot act on.
 */
export function DecodedSlide({ item }: { item: Extract<SpotlightItem, { kind: "decodable" }> }) {
  const decoded = useDecodedImage(item.url, item.contentType);

  return (
    <figure className="spotlight__frame">
      {decoded.kind === "shown" ? (
        <img className="spotlight__decoded" src={decoded.url} alt={item.caption} />
      ) : (
        <div className="spotlight__unavailable">
          <p className="spotlight__reason" role={decoded.kind === "decoding" ? "status" : undefined}>
            {decoded.kind === "decoding"
              ? SPOTLIGHT_COPY.converting(item.contentType)
              : SPOTLIGHT_COPY.conversionFailed(item.contentType)}
          </p>
          {decoded.kind === "failed" && (
            <a
              className="spotlight__download"
              href={item.url}
              target="_blank"
              rel="noreferrer"
              download
            >
              {SPOTLIGHT_COPY.download}
            </a>
          )}
        </div>
      )}
      <figcaption className="spotlight__caption">
        {item.caption}
        {/* The original bytes, always — what was converted is a rendering of
            the document and the file itself is what a reader may need to keep. */}
        <a href={item.url} target="_blank" rel="noreferrer">
          {SPOTLIGHT_COPY.openInTab}
        </a>
      </figcaption>
    </figure>
  );
}
